import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Download, 
  Upload,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  FileText,
  Activity,
  BarChart3,
  X,
  Building2,
  Radio
} from 'lucide-react';

const BudgetRealizationList = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalBudgetFO: 0,
    totalRealizationFO: 0,
    varianceFO: 0,
    totalBudgetTower: 0,
    totalRealizationTower: 0,
    varianceTower: 0,
    totalPO: 0,
    paidPO: 0,
    partialPO: 0,
    unpaidPO: 0
  });

  useEffect(() => {
    loadMockData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [data, searchTerm, selectedProject, selectedType, selectedStatus, selectedPaymentStatus, dateFrom, dateTo]);

  useEffect(() => {
    calculateSummary();
  }, [filteredData]);

  const loadMockData = () => {
    // Mock projects
    const mockProjects = [
      { id: 1, projectName: 'Telkomsel Jakarta Selatan Tower Project', projectCode: 'TSEL-JKT-2025-001', type: 'TOWER' },
      { id: 2, projectName: 'IOH Fiber Optic Jakarta-Bandung', projectCode: 'IOH-FO-2025-001', type: 'FO' },
      { id: 3, projectName: 'XL Smart Tower Expansion Surabaya', projectCode: 'XLS-TWR-2025-001', type: 'TOWER' },
      { id: 4, projectName: 'BAKTI Palapa Ring Segment', projectCode: 'BAKTI-FO-2025-001', type: 'FO' }
    ];
    setProjects(mockProjects);

    // Mock PO data
    const mockData = [
      {
        id: 1,
        poNumber: 'PO-2025-001',
        poDate: '2025-01-10',
        projectId: 1,
        projectName: 'Telkomsel Jakarta Selatan',
        projectType: 'TOWER',
        itemName: 'JKT-South-001',
        description: 'Material tower dan foundation',
        vendor: 'PT Supplier Tower',
        poAmount: 500000000,
        paidAmount: 500000000,
        remainingAmount: 0,
        paymentStatus: 'PAID',
        paymentPercentage: 100,
        lastPaymentDate: '2025-01-15',
        dueDate: '2025-02-10',
        status: 'COMPLETED',
        paymentHistory: [
          { date: '2025-01-15', amount: 500000000, note: 'Full payment' }
        ]
      },
      {
        id: 2,
        poNumber: 'PO-2025-002',
        poDate: '2025-01-12',
        projectId: 1,
        projectName: 'Telkomsel Jakarta Selatan',
        projectType: 'TOWER',
        itemName: 'JKT-South-002',
        description: 'Equipment dan instalasi tower',
        vendor: 'PT Telekom Equipment',
        poAmount: 450000000,
        paidAmount: 225000000,
        remainingAmount: 225000000,
        paymentStatus: 'PARTIAL',
        paymentPercentage: 50,
        lastPaymentDate: '2025-01-18',
        dueDate: '2025-02-15',
        status: 'IN_PROGRESS',
        paymentHistory: [
          { date: '2025-01-18', amount: 225000000, note: 'DP 50%' }
        ]
      },
      {
        id: 3,
        poNumber: 'PO-2025-003',
        poDate: '2025-01-14',
        projectId: 1,
        projectName: 'Telkomsel Jakarta Selatan',
        projectType: 'TOWER',
        itemName: 'JKT-South-003',
        description: 'Civil work dan persiapan lahan',
        vendor: 'PT Konstruksi Sejahtera',
        poAmount: 350000000,
        paidAmount: 0,
        remainingAmount: 350000000,
        paymentStatus: 'UNPAID',
        paymentPercentage: 0,
        lastPaymentDate: null,
        dueDate: '2025-02-20',
        status: 'PENDING',
        paymentHistory: []
      },
      {
        id: 4,
        poNumber: 'PO-2025-004',
        poDate: '2025-01-08',
        projectId: 2,
        projectName: 'IOH Fiber Optic Jakarta-Bandung',
        projectType: 'FO',
        itemName: 'Segment A',
        description: 'Fiber optic cable installation',
        vendor: 'PT Fiber Teknologi',
        poAmount: 800000000,
        paidAmount: 800000000,
        remainingAmount: 0,
        paymentStatus: 'PAID',
        paymentPercentage: 100,
        lastPaymentDate: '2025-01-20',
        dueDate: '2025-02-08',
        status: 'COMPLETED',
        paymentHistory: [
          { date: '2025-01-12', amount: 400000000, note: 'DP 50%' },
          { date: '2025-01-20', amount: 400000000, note: 'Pelunasan' }
        ]
      },
      {
        id: 5,
        poNumber: 'PO-2025-005',
        poDate: '2025-01-11',
        projectId: 2,
        projectName: 'IOH Fiber Optic Jakarta-Bandung',
        projectType: 'FO',
        itemName: 'Segment B',
        description: 'Conduit and duct installation',
        vendor: 'PT Infrastruktur Kabel',
        poAmount: 650000000,
        paidAmount: 390000000,
        remainingAmount: 260000000,
        paymentStatus: 'PARTIAL',
        paymentPercentage: 60,
        lastPaymentDate: '2025-01-19',
        dueDate: '2025-02-18',
        status: 'IN_PROGRESS',
        paymentHistory: [
          { date: '2025-01-15', amount: 195000000, note: 'DP 30%' },
          { date: '2025-01-19', amount: 195000000, note: 'Termin 1' }
        ]
      },
      {
        id: 6,
        poNumber: 'PO-2025-006',
        poDate: '2025-01-16',
        projectId: 2,
        projectName: 'IOH Fiber Optic Jakarta-Bandung',
        projectType: 'FO',
        itemName: 'Segment C',
        description: 'Splicing and testing equipment',
        vendor: 'PT Network Solutions',
        poAmount: 450000000,
        paidAmount: 0,
        remainingAmount: 450000000,
        paymentStatus: 'UNPAID',
        paymentPercentage: 0,
        lastPaymentDate: null,
        dueDate: '2025-02-25',
        status: 'PENDING',
        paymentHistory: []
      },
      {
        id: 7,
        poNumber: 'PO-2025-007',
        poDate: '2025-01-09',
        projectId: 3,
        projectName: 'XL Smart Tower Expansion',
        projectType: 'TOWER',
        itemName: 'SBY-East-001',
        description: 'Tower structure and assembly',
        vendor: 'PT Menara Teknologi',
        poAmount: 420000000,
        paidAmount: 420000000,
        remainingAmount: 0,
        paymentStatus: 'PAID',
        paymentPercentage: 100,
        lastPaymentDate: '2025-01-17',
        dueDate: '2025-02-09',
        status: 'COMPLETED',
        paymentHistory: [
          { date: '2025-01-17', amount: 420000000, note: 'Full payment' }
        ]
      },
      {
        id: 8,
        poNumber: 'PO-2025-008',
        poDate: '2025-01-13',
        projectId: 3,
        projectName: 'XL Smart Tower Expansion',
        projectType: 'TOWER',
        itemName: 'SBY-East-002',
        description: 'Power system and backup',
        vendor: 'PT Power Systems',
        poAmount: 380000000,
        paidAmount: 152000000,
        remainingAmount: 228000000,
        paymentStatus: 'PARTIAL',
        paymentPercentage: 40,
        lastPaymentDate: '2025-01-20',
        dueDate: '2025-02-22',
        status: 'IN_PROGRESS',
        paymentHistory: [
          { date: '2025-01-20', amount: 152000000, note: 'DP 40%' }
        ]
      },
      {
        id: 9,
        poNumber: 'PO-2025-009',
        poDate: '2025-01-07',
        projectId: 4,
        projectName: 'BAKTI Palapa Ring',
        projectType: 'FO',
        itemName: 'Palapa-1',
        description: 'Submarine cable installation',
        vendor: 'PT Marine Cable Systems',
        poAmount: 1200000000,
        paidAmount: 960000000,
        remainingAmount: 240000000,
        paymentStatus: 'PARTIAL',
        paymentPercentage: 80,
        lastPaymentDate: '2025-01-21',
        dueDate: '2025-02-28',
        status: 'IN_PROGRESS',
        paymentHistory: [
          { date: '2025-01-10', amount: 480000000, note: 'DP 40%' },
          { date: '2025-01-21', amount: 480000000, note: 'Termin 1' }
        ]
      },
      {
        id: 10,
        poNumber: 'PO-2025-010',
        poDate: '2025-01-15',
        projectId: 4,
        projectName: 'BAKTI Palapa Ring',
        projectType: 'FO',
        itemName: 'Palapa-2',
        description: 'Landing station equipment',
        vendor: 'PT Telekom Equipment',
        poAmount: 850000000,
        paidAmount: 0,
        remainingAmount: 850000000,
        paymentStatus: 'UNPAID',
        paymentPercentage: 0,
        lastPaymentDate: null,
        dueDate: '2025-03-05',
        status: 'PENDING',
        paymentHistory: []
      }
    ];

    setData(mockData);
    setFilteredData(mockData);
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedProject) {
      filtered = filtered.filter(item => item.projectId === parseInt(selectedProject));
    }

    if (selectedType) {
      filtered = filtered.filter(item => item.projectType === selectedType);
    }

    if (selectedStatus) {
      filtered = filtered.filter(item => item.status === selectedStatus);
    }

    if (selectedPaymentStatus) {
      filtered = filtered.filter(item => item.paymentStatus === selectedPaymentStatus);
    }

    if (dateFrom) {
      filtered = filtered.filter(item => item.poDate >= dateFrom);
    }

    if (dateTo) {
      filtered = filtered.filter(item => item.poDate <= dateTo);
    }

    setFilteredData(filtered);
  };

  const calculateSummary = () => {
    const foData = data.filter(item => item.projectType === 'FO');
    const towerData = data.filter(item => item.projectType === 'TOWER');

    const stats = {
      totalBudgetFO: foData.reduce((sum, item) => sum + item.poAmount, 0),
      totalRealizationFO: foData.reduce((sum, item) => sum + item.paidAmount, 0),
      varianceFO: foData.reduce((sum, item) => sum + item.remainingAmount, 0),
      totalBudgetTower: towerData.reduce((sum, item) => sum + item.poAmount, 0),
      totalRealizationTower: towerData.reduce((sum, item) => sum + item.paidAmount, 0),
      varianceTower: towerData.reduce((sum, item) => sum + item.remainingAmount, 0),
      totalPO: data.length,
      paidPO: data.filter(item => item.paymentStatus === 'PAID').length,
      partialPO: data.filter(item => item.paymentStatus === 'PARTIAL').length,
      unpaidPO: data.filter(item => item.paymentStatus === 'UNPAID').length
    };

    setSummaryStats(stats);
  };

  const handleViewDetail = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProject('');
    setSelectedType('');
    setSelectedStatus('');
    setSelectedPaymentStatus('');
    setDateFrom('');
    setDateTo('');
  };

  const getPaymentStatusBadge = (status, percentage) => {
    const config = {
      PAID: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: 'Paid' },
      PARTIAL: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', label: 'Partial' },
      UNPAID: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: 'Unpaid' }
    };

    const { bg, text, border, label } = config[status] || config.UNPAID;

    return (
      <div className="flex items-center gap-2">
        <span className={`px-3 py-1 text-xs font-bold rounded-full border ${bg} ${text} ${border}`}>
          {label}
        </span>
        <span className="text-xs font-semibold text-gray-600">{percentage}%</span>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      COMPLETED: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: CheckCircle },
      IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', icon: Clock },
      PENDING: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', icon: AlertCircle }
    };

    const { bg, text, border, icon: Icon } = config[status] || config.PENDING;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${bg} ${text} ${border}`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Budget Realization</h1>
            <p className="text-gray-600 mt-1">Monitor PO dan pembayaran budget project</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md hover:shadow-lg transition-all"
            >
              <Upload className="w-4 h-4" />
              Upload Data
            </button>
            <button
              onClick={() => window.location.href = '/monitoring/budget-analysis'}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow-md hover:shadow-lg transition-all"
            >
              <BarChart3 className="w-4 h-4" />
              Budget Analysis
            </button>
            <Activity className="w-6 h-6 text-red-600" />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* FO Project Budget Card */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Radio className="w-6 h-6" />
            </div>
            <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">FO PROJECT</span>
          </div>
          <div className="mb-4">
            <div className="text-sm text-green-100 mb-1">Total Budget</div>
            <div className="text-2xl font-bold">{formatCurrency(summaryStats.totalBudgetFO)}</div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white border-opacity-20">
            <div>
              <div className="text-xs text-green-100 mb-1">Realization</div>
              <div className="text-lg font-bold">{formatCurrency(summaryStats.totalRealizationFO)}</div>
              <div className="text-xs text-green-100 mt-1">
                {calculatePercentage(summaryStats.totalRealizationFO, summaryStats.totalBudgetFO)}%
              </div>
            </div>
            <div>
              <div className="text-xs text-green-100 mb-1">Remaining</div>
              <div className="text-lg font-bold">{formatCurrency(summaryStats.varianceFO)}</div>
              <div className="text-xs text-green-100 mt-1">
                {calculatePercentage(summaryStats.varianceFO, summaryStats.totalBudgetFO)}%
              </div>
            </div>
          </div>
        </div>

        {/* Tower Project Budget Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">TOWER PROJECT</span>
          </div>
          <div className="mb-4">
            <div className="text-sm text-blue-100 mb-1">Total Budget</div>
            <div className="text-2xl font-bold">{formatCurrency(summaryStats.totalBudgetTower)}</div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white border-opacity-20">
            <div>
              <div className="text-xs text-blue-100 mb-1">Realization</div>
              <div className="text-lg font-bold">{formatCurrency(summaryStats.totalRealizationTower)}</div>
              <div className="text-xs text-blue-100 mt-1">
                {calculatePercentage(summaryStats.totalRealizationTower, summaryStats.totalBudgetTower)}%
              </div>
            </div>
            <div>
              <div className="text-xs text-blue-100 mb-1">Remaining</div>
              <div className="text-lg font-bold">{formatCurrency(summaryStats.varianceTower)}</div>
              <div className="text-xs text-blue-100 mt-1">
                {calculatePercentage(summaryStats.varianceTower, summaryStats.totalBudgetTower)}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PO Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total PO</p>
              <p className="text-2xl font-bold text-gray-800">{summaryStats.totalPO}</p>
            </div>
            <FileText className="w-8 h-8 text-gray-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Paid</p>
              <p className="text-2xl font-bold text-green-600">{summaryStats.paidPO}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Partial</p>
              <p className="text-2xl font-bold text-yellow-600">{summaryStats.partialPO}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Unpaid</p>
              <p className="text-2xl font-bold text-red-600">{summaryStats.unpaidPO}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {/* Search and Filter Bar */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by PO number, project, vendor, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`px-4 py-2 border-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                showFilterPanel 
                  ? 'border-red-600 bg-red-50 text-red-600' 
                  : 'border-gray-300 text-gray-700 hover:border-red-500 hover:bg-red-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {(selectedProject || selectedType || selectedStatus || selectedPaymentStatus || dateFrom || dateTo) && (
                <span className="ml-1 px-2 py-0.5 bg-red-600 text-white rounded-full text-xs font-bold">
                  Active
                </span>
              )}
            </button>
            <button 
              onClick={() => window.print()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>

          {/* Filter Panel */}
          {showFilterPanel && (
            <div className="mt-4 p-4 bg-white border-2 border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Project</label>
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  >
                    <option value="">All Projects</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.projectName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  >
                    <option value="">All Types</option>
                    <option value="FO">FO Segment</option>
                    <option value="TOWER">Tower Site</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">PO Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="PENDING">Pending</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Payment Status</label>
                  <select
                    value={selectedPaymentStatus}
                    onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  >
                    <option value="">All Payment</option>
                    <option value="PAID">Paid</option>
                    <option value="PARTIAL">Partial</option>
                    <option value="UNPAID">Unpaid</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Date From</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Date To</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-red-600 to-red-500 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">PO Number</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">PO Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Project</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Item</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">PO Amount</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Paid</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Remaining</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-6 py-12 text-center">
                    <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No data found</p>
                    <p className="text-gray-400 text-sm">Try adjusting your filters</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-red-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-bold text-blue-600">{item.poNumber}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-900">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(item.poDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.projectName}</div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${
                        item.projectType === 'FO' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {item.projectType === 'TOWER' ? (
                          <Building2 className="w-3 h-3 mr-1" />
                        ) : (
                          <Radio className="w-3 h-3 mr-1" />
                        )}
                        {item.projectType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">{item.itemName}</div>
                      <div className="text-xs text-gray-500">{item.description}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{item.vendor}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">{formatCurrency(item.poAmount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-green-600">{formatCurrency(item.paidAmount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-red-600">{formatCurrency(item.remainingAmount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentStatusBadge(item.paymentStatus, item.paymentPercentage)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetail(item)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Showing <span className="font-bold text-red-600">{filteredData.length}</span> of <span className="font-bold">{data.length}</span> PO
            </div>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-bold border border-green-300">PAID</span>
                <span className="text-gray-600">{summaryStats.paidPO}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-bold border border-yellow-300">PARTIAL</span>
                <span className="text-gray-600">{summaryStats.partialPO}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-bold border border-red-300">UNPAID</span>
                <span className="text-gray-600">{summaryStats.unpaidPO}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">PO Details - {selectedItem.poNumber}</h2>
                  <p className="text-red-100 text-sm mt-1">{selectedItem.projectName}</p>
                </div>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedItem(null);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* PO Information */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">PO Number</label>
                  <p className="text-gray-900 font-bold">{selectedItem.poNumber}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">PO Date</label>
                  <p className="text-gray-900">{formatDate(selectedItem.poDate)}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Project</label>
                  <p className="text-gray-900">{selectedItem.projectName}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Item</label>
                  <p className="text-gray-900">{selectedItem.itemName}</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded border">{selectedItem.description}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor</label>
                  <p className="text-gray-900">{selectedItem.vendor}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Due Date</label>
                  <p className="text-gray-900">{formatDate(selectedItem.dueDate)}</p>
                </div>
              </div>

              {/* Financial Information */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-blue-900 mb-4">Financial Summary</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">PO Amount</label>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedItem.poAmount)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">Paid Amount</label>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedItem.paidAmount)}</p>
                    <p className="text-sm text-gray-600 mt-1">{selectedItem.paymentPercentage}% paid</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">Remaining</label>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(selectedItem.remainingAmount)}</p>
                    <p className="text-sm text-gray-600 mt-1">{100 - selectedItem.paymentPercentage}% remaining</p>
                  </div>
                </div>
              </div>

              {/* Payment History */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Payment History</h3>
                {selectedItem.paymentHistory.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No payment recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedItem.paymentHistory.map((payment, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
                            <p className="text-sm text-gray-600">{payment.note}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{formatDate(payment.date)}</p>
                          <CheckCircle className="w-5 h-5 text-green-600 ml-auto mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Status</label>
                  {getPaymentStatusBadge(selectedItem.paymentStatus, selectedItem.paymentPercentage)}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">PO Status</label>
                  {getStatusBadge(selectedItem.status)}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedItem(null);
                }}
                className="px-5 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal Placeholder */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Upload Data PO & Payment</h2>
                  <p className="text-blue-100 text-sm mt-1">Upload Excel files from accounting system</p>
                </div>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">Upload feature coming soon</p>
                <p className="text-sm text-gray-500">Will support Excel import for PO and Payment data</p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-5 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetRealizationList;